<script setup>
import { ref } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  onClose: {
    type: Function,
    default: () => {},
  },
});

const { t } = useI18n();
const store = useStore();

const shortCode = ref('');
const content = ref('');
const attachments = ref([null]);
const showLoading = ref(false);

const rules = {
  shortCode: { required, minLength: minLength(2) },
  content: { required },
};

const v$ = useVuelidate(rules, { shortCode, content });

function resetForm() {
  shortCode.value = '';
  content.value = '';
  attachments.value = [null];
  v$.value.shortCode.$reset();
  v$.value.content.$reset();
}

function onAttachmentChange(index, event) {
  const [file] = event.target.files;
  attachments.value.splice(index, 1, file || null);
}

function addAttachmentRow() {
  attachments.value.push(null);
}

function removeAttachmentRow(index) {
  attachments.value.splice(index, 1);
  if (!attachments.value.length) {
    addAttachmentRow();
  }
}

async function addCannedResponse() {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  showLoading.value = true;
  try {
    await store.dispatch('createCannedResponse', {
      short_code: shortCode.value,
      content: content.value,
      attachments: attachments.value.filter(Boolean),
    });
    showLoading.value = false;
    useAlert(t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
    resetForm();
    props.onClose();
  } catch (error) {
    showLoading.value = false;
    const errorMessage =
      error?.message || t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
}
</script>

<template>
  <div class="flex max-h-[85vh] flex-col overflow-auto">
    <woot-modal-header
      compact
      :header-title="$t('CANNED_MGMT.ADD.TITLE')"
      :header-content="$t('CANNED_MGMT.ADD.INTRO')"
    />
    <form
      class="modal-content flex w-full flex-col gap-3 !px-5 !pb-5 !pt-2"
      @submit.prevent="addCannedResponse"
    >
      <div class="flex flex-col gap-3 border-b border-n-weak pb-3">
        <div class="flex flex-col gap-1">
          <label
            class="mb-0 text-xs font-medium text-n-slate-12"
            :class="{ 'text-n-ruby-9': v$.shortCode.$error }"
          >
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
          </label>
          <Input
            v-model="shortCode"
            size="sm"
            type="text"
            :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
            @blur="v$.shortCode.$touch"
          />
        </div>

        <div class="flex flex-col gap-1">
          <label
            class="mb-0 text-xs font-medium text-n-slate-12"
            :class="{ 'text-n-ruby-9': v$.content.$error }"
          >
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div
            class="[&_.ProseMirror-menubar]:hidden [&_.ProseMirror-woot-style]:min-h-[8rem] [&_.ProseMirror-woot-style_p]:text-sm"
          >
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{
                'outline outline-1 outline-n-ruby-9 rounded-xl':
                  v$.content.$error,
              }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label class="mb-0 text-xs font-medium text-n-slate-12">
          {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENT.LABEL') }}
        </label>
        <div
          class="flex flex-col gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-black2 px-2.5 py-3"
        >
          <div
            v-for="(_attachment, index) in attachments"
            :key="index"
            class="flex flex-wrap items-center gap-2"
          >
            <span class="w-10 shrink-0 text-xs font-medium text-n-slate-11">
              {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENT.FILE_LABEL') }}
            </span>
            <input
              type="file"
              class="min-w-0 flex-1 cursor-pointer rounded-md border-0 bg-n-solid-1 px-2 py-1.5 text-xs text-n-slate-12 outline outline-1 outline-n-weak file:mr-2 file:rounded-md file:border-0 file:bg-n-slate-3 file:px-2 file:py-1 file:text-xs file:font-medium file:text-n-slate-12"
              :aria-label="$t('CANNED_MGMT.ADD.FORM.ATTACHMENT.FILE_LABEL')"
              @change="onAttachmentChange(index, $event)"
            />
            <NextButton
              v-if="attachments.length > 1"
              type="button"
              slate
              ghost
              size="sm"
              icon="i-lucide-trash"
              @click.prevent="removeAttachmentRow(index)"
            />
          </div>
          <NextButton
            type="button"
            faded
            slate
            size="sm"
            class="w-full border border-dashed border-n-weak bg-transparent"
            icon="i-lucide-plus"
            :label="$t('CANNED_MGMT.ADD.FORM.ATTACHMENT.ADD_FILE')"
            @click.prevent="addAttachmentRow"
          />
        </div>
        <p class="mb-0 text-xs leading-snug text-n-slate-11">
          {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENT.HELP_TEXT') }}
        </p>
      </div>

      <div class="flex flex-row justify-end gap-2 border-t border-n-weak pt-3">
        <NextButton
          faded
          slate
          size="sm"
          type="button"
          :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          blue
          solid
          size="sm"
          :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
          :disabled="
            v$.content.$invalid || v$.shortCode.$invalid || showLoading
          "
          :is-loading="showLoading"
        />
      </div>
    </form>
  </div>
</template>
